import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface TimelineEvent {
  id: string;
  type: 'mood' | 'reminder' | 'okay' | 'chat';
  time: string;
  label: string;
  detail?: string;
  icon: string;
}

export default function CareTimeline() {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [seniorName, setSeniorName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    async function loadTimeline() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const today = new Date().toISOString().split('T')[0];

        // Get senior linked to this family member
        const { data: connection } = await supabase
          .from('care_circle_members')
          .select('senior_id, profiles(full_name)')
          .eq('family_user_id', user.id)
          .eq('status', 'active')
          .limit(1)
          .single();

      if (!connection) {
        setSeniorName('Your Loved One');
        setLoading(false);
        return;
      }
        const seniorId = connection.senior_id;
        setSeniorName((connection.profiles as any)?.full_name || 'Your Loved One');

        const allEvents: TimelineEvent[] = [];

        // Mood logs
        const { data: moods } = await supabase
          .from('mood_logs')
          .select('id, mood, created_at')
          .eq('user_id', seniorId)
          .gte('created_at', today)
          .order('created_at', { ascending: true });

        moods?.forEach(m => allEvents.push({
          id: `mood-${m.id}`,
          type: 'mood',
          time: m.created_at,
          label: 'Logged mood',
          detail: m.mood,
          icon: '😊'
        }));

        // Reminders completed
        const { data: reminders } = await supabase
          .from('reminders')
          .select('id, title, completed_at')
          .eq('user_id', seniorId)
          .eq('completed', true)
          .gte('completed_at', today)
          .order('completed_at', { ascending: true });

        reminders?.forEach(r => allEvents.push({
          id: `reminder-${r.id}`,
          type: 'reminder',
          time: r.completed_at,
          label: 'Completed reminder',
          detail: r.title,
          icon: '✅'
        }));

        // I'm okay check-in
        const { data: okay } = await supabase
          .from('activity_log')
          .select('id, created_at')
          .eq('user_id', seniorId)
          .eq('action', 'okay_today')
          .gte('created_at', today)
          .limit(1);

        okay?.forEach(o => allEvents.push({
          id: `okay-${o.id}`,
          type: 'okay',
          time: o.created_at,
          label: "Tapped \"I'm okay today\"",
          icon: '👍'
        }));

        // CareChat messages
        const { data: chats } = await supabase
          .from('care_messages')
          .select('id, message, created_at, sender_role')
          .eq('senior_id', seniorId)
          .gte('created_at', today)
          .order('created_at', { ascending: true });

        chats?.forEach(c => allEvents.push({
          id: `chat-${c.id}`,
          type: 'chat',
          time: c.created_at,
          label: c.sender_role === 'senior' ? 'Sent a message' : 'Family sent a message',
          detail: c.message,
          icon: '💬'
        }));

        // Sort all events by time
        allEvents.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
        setEvents(allEvents);
      } catch (err) {
        console.error('[CareTimeline] load failed:', err);
      } finally {
        setLoading(false);
      }
    }

    loadTimeline();
  }, []);

  function formatTime(iso: string) {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0F172A', color: 'var(--admin-text-primary)', padding: '24px 16px' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <button onClick={() => navigate(-1)} style={{ background: 'transparent', border: 'none', color: 'var(--admin-accent)', cursor: 'pointer', marginBottom: '16px', fontSize: '0.9rem' }}>
          ← Back
        </button>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '800', marginBottom: '4px' }}>Care Timeline</h1>
        <p style={{ color: 'var(--admin-text-secondary)', marginBottom: '32px', fontSize: '0.9rem' }}>
          {seniorName}'s activity today — {new Date().toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>

        {loading && <p style={{ color: '#64748B' }}>Loading...</p>}

        {!loading && events.length === 0 && (
          <div style={{ background: 'var(--admin-surface)', borderRadius: '12px', padding: '32px', textAlign: 'center' }}>
            <p style={{ fontSize: '2rem', marginBottom: '8px' }}>📋</p>
            <p style={{ color: 'var(--admin-text-secondary)' }}>No activity logged yet today.</p>
          </div>
        )}

        {!loading && events.length > 0 && (
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: '20px', top: 0, bottom: 0, width: '2px', background: 'var(--admin-border)' }} />
            {events.map(event => (
              <div key={event.id} style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '50%',
                  background: 'var(--admin-surface)', border: '2px solid var(--admin-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.1rem', flexShrink: 0, zIndex: 1
                }}>
                  {event.icon}
                </div>
                <div style={{ background: 'var(--admin-surface)', borderRadius: '10px', padding: '12px 16px', flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>{event.label}</span>
                    <span style={{ color: '#64748B', fontSize: '0.8rem' }}>{formatTime(event.time)}</span>
                  </div>
                  {event.detail && <p style={{ color: 'var(--admin-text-secondary)', fontSize: '0.85rem', margin: 0 }}>{event.detail}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
